import { formatNumber } from "@/v2-common/utils/utils.formatting"
import type { UsageProfileFragment } from "@/v2-backoffice-client/usage/__types__/usage.fragment"
import type { UsageAppMixEntry } from "@/v2-backoffice-client/usage/UsageAppMix"
import {
  formatAppArea,
  formatAppCode,
  getScoreLevel,
  isMcpAppCode,
  type ScoreLevel,
  formatMunicipalityName,
} from "@/v2-backoffice-client/usage/usage.constants"
import {
  describeTrend,
  formatShare,
  list,
} from "@/v2-backoffice-client/usage/usage.text"

/** "A, B and C" */

/**
 * One person's usage in a paragraph or two, from their profile and product
 * mix: how engaged, how much and how often, what the work is about and
 * where, and which products carry it.
 */
/** The score level as a noun phrase: "reads as a power user", "… as dormant". */
const SCORE_LEVEL_PHRASES: Record<ScoreLevel, string> = {
  power: "a power user",
  healthy: "a healthy user",
  cooling: "cooling",
  dormant: "dormant",
  new: "unscored",
}

export function deriveUserStory(input: {
  profile: UsageProfileFragment
  appMix: UsageAppMixEntry[]
  subject: string
}): string[] {
  const { profile, appMix, subject } = input
  const paragraphs: string[] = []

  {
    const sentences: string[] = []
    const level = getScoreLevel(profile.EngagementScore)
    const weighted =
      profile.WeightedEvents28d != null
        ? Math.round(profile.WeightedEvents28d)
        : profile.Events28d
    const trend = profile.WeightedTrendPct ?? profile.TrendPct
    sentences.push(
      `${subject} ${level === "new" ? "has no engagement score yet" : `reads as ${SCORE_LEVEL_PHRASES[level]}`}: ${formatNumber(weighted)} weighted actions in the last 28 days${
        trend != null
          ? `, ${describeTrend(trend)} on the four weeks before`
          : ""
      }, on ${profile.ActiveDays28d} of the last 28 days.`,
    )
    paragraphs.push(sentences.join(" "))
  }

  {
    const sentences: string[] = []
    const areas = profile.TopAreas28d.slice(0, 3)
    if (areas.length > 0) {
      sentences.push(
        `The work is mostly ${list(areas.map((area) => formatAppArea(area.Area)))}${
          profile.TopGeoAreas28d[0]
            ? `, concentrated in ${list(
                profile.TopGeoAreas28d.slice(0, 2).map((geo) =>
                  formatMunicipalityName(geo.Name),
                ),
              )}`
            : ""
        }.`,
      )
    }
    const total = appMix.reduce((sum, entry) => sum + entry.weighted28d, 0)
    const mcp = appMix.filter(
      (entry) => isMcpAppCode(entry.app) && entry.weighted28d > 0,
    )
    const v1 = appMix.find((entry) => entry.app === "v1-console")
    if (total > 0 && mcp.length > 0) {
      const share = Math.round(
        (mcp.reduce((sum, entry) => sum + entry.weighted28d, 0) * 100) / total,
      )
      sentences.push(
        `${formatShare(share)} of it runs through ${list(mcp.map((entry) => formatAppCode(entry.app)))} from an AI client.`,
      )
    }
    if (v1 && total > 0 && v1.weighted28d > 0) {
      sentences.push(
        `${formatShare(Math.round((v1.weighted28d * 100) / total))} still happens in Console v1.`,
      )
    }
    if (sentences.length > 0) paragraphs.push(sentences.join(" "))
  }

  return paragraphs
}
